import React, { useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { InputAdornment, Stack } from '@mui/material';
import styles from './SdkApplications.module.scss';
import { SearchOutlined } from '@mui/icons-material';
import * as PATHS from 'js/constants/pathnames';
import { ScreenComponent } from 'new/routes/RouterManager';
import { getFeatureFlagValue } from 'new/utils/FeatureUtil';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { Table } from 'new/elements/Table';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import { ChipBadge } from 'new/elements/ChipBadge';
import { useModals } from 'new/hooks/useModals';
import { useDistance } from 'new/hooks/useDistance';
import { filterBy } from 'new/utils/filterBy';
import { useSdkApplications } from 'new/components/settings/SdkApplications/useSdkApplications';
import { columns } from 'new/components/settings/SdkApplications/columns';
import { SdkApplicationActions } from 'new/components/settings/SdkApplications/SdkApplicationActions';
import { SdkApplicationModal } from 'new/components/settings/SdkApplications/SdkApplicationModal';
import { SdkApplication } from '@nexla/@types';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';

export const SdkApplications: ScreenComponent<{}> = () => {
  const { data, isLoading, create, update, remove, pause, activate, rotateLinkedKeys } =
    useSdkApplications();
  const { openModal } = useModals();
  const [searchQuery, setSearchQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const distance = useDistance(ref, 500);

  const filtered = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter(filterBy(searchQuery, 'name', 'description'));
  }, [data, searchQuery]);

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const onClickRegister = () => {
    openModal(SdkApplicationModal.modalConfig.name, {
      mode: 'create',
      createApp: create,
    });
  };

  return (
    <PageLayout
      ref={ref}
      title="SDK Applications"
      titleAccessory={
        !isLoading ? (
          <ChipBadge
            color="disabled"
            label={`${data.length} ${data.length === 1 ? 'application' : 'applications'}`}
          />
        ) : null
      }
      bottomView={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          width="100%"
          marginTop={3}
        >
          <TextField
            placeholder="Find applications by name or description"
            onChange={onSearchChange}
            className={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={onClickRegister}
            className={styles.addApplicationButton}
            startIcon={<PlusIcon />}
          >
            Register Application
          </Button>
        </Stack>
      }
    >
      <Table
        height={distance}
        columns={columns}
        rows={filtered}
        loading={isLoading}
        rowEndRender={(row: SdkApplication) => (
          <SdkApplicationActions
            row={row}
            update={update}
            remove={remove}
            pause={pause}
            activate={activate}
            rotateLinkedKeys={rotateLinkedKeys}
          />
        )}
      />
    </PageLayout>
  );
};

SdkApplications.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/sdk-applications`,
  modals: [SdkApplicationModal],
  screenName: 'SdkApplications',
  pageTitle: 'SDK Applications',
  header: {
    title: 'Settings',
    slugTitle: 'SDK Applications',
  },
  domain: 'settings',
};
